/**

 * Aspect — AgentClient (browser -> HTTP orchestrator)

 * --------------------------------------------------------------

 * Sends a natural-language prompt to the orchestrator's `/agent` endpoint and

 * streams the Server-Sent-Events tool activity back as app events. The actual

 * furniture changes arrive separately over the WebSocket bridge (the agent calls

 * staging tools that emit spawn/remove commands), so this module only drives the

 * status UI — it never touches the DOM directly.

 *

 * Named wall finishes and style themes apply locally first so SampleTour restyle

 * still works when the LLM call fails.

 */



import { getAiProvider } from "./ai_provider.js";

import { orchUrl } from "./endpoints.js";

import { parseRestyleIntent, orchUserError } from "./restyle_intent.js";



const ORCH_URL = orchUrl();



const PRETTY = {

  get_spatial_constraints: "Reading the room…",

  validate_clearance: "Checking the fit…",

  clear_room: "Clearing the room…",

  apply_style_theme: "Restyling the home…",

  set_surface_finish: "Updating finishes…",

};



export class AgentClient {

  /** @param {import('playcanvas').AppBase} app */

  constructor(app, opts = {}) {

    this.app = app;

    this.url = opts.url ?? ORCH_URL;

    this.busy = false;

    this.getSnapshot = opts.getSnapshot ?? null;

  }



  async run(prompt) {

    if (this.busy || !prompt) return;

    this.busy = true;

    this.app.fire("scene:sandbox-expect", {});

    this.app.fire("ai:status", { text: "Thinking…", busy: true });



    const intent = parseRestyleIntent(prompt);

    let localDone = false;

    try {

      if (intent.theme) {

        localDone = await this._applyTheme(intent.theme);

        if (localDone) {

          this.app.fire("ai:status", { text: `Restyled — ${intent.theme}.`, busy: false, done: true });

          return;

        }

      }

      if (Object.keys(intent.finishes).length) {

        this._applyFinishes(intent.finishes);

        localDone = true;

        if (intent.finishOnly) {

          const wall = intent.finishes.wall ? "Walls updated." : "Floors updated.";

          this.app.fire("ai:status", { text: wall, busy: false, done: true });

          return;

        }

      }



      const res = await fetch(`${this.url}/agent`, {

        method: "POST",

        headers: { "content-type": "application/json" },

        credentials: "include",

        body: JSON.stringify({

          prompt,

          provider: getAiProvider(),

          snapshot: this.getSnapshot?.() ?? null,

        }),

      });

      if (!res.ok || !res.body) throw new Error(`orchestrator ${res.status}`);



      const reader = res.body.getReader();

      const decoder = new TextDecoder();

      let buf = "";

      let placed = 0;



      for (;;) {

        const { value, done } = await reader.read();

        if (done) break;

        buf += decoder.decode(value, { stream: true });

        let sep;

        while ((sep = buf.indexOf("\n\n")) >= 0) {

          const raw = buf.slice(0, sep);

          buf = buf.slice(sep + 2);

          placed += this._handle(raw);

        }

      }



      const summary = placed > 0 ? `Done — updated ${placed} item${placed === 1 ? "" : "s"}.` : "Done.";

      this.app.fire("ai:status", { text: summary, busy: false, done: true });

    } catch (err) {

      const hint = localDone

        ? "Style applied here. Furniture restyle needs the AI service — try Send again."

        : orchUserError(err, Number(/orchestrator (\d+)/.exec(err.message)?.[1] || 0));

      this.app.fire("ai:status", { text: `Error: ${hint}`, busy: false, error: true });

    } finally {

      this.busy = false;

    }

  }



  _applyFinishes(finishes) {

    for (const [role, finishId] of Object.entries(finishes)) {

      if (finishId) this.app.fire("ui:set-finish", { role, finishId });

    }

  }



  async _applyTheme(themeId) {

    try {

      const res = await fetch(`${this.url}/apply-theme`, {

        method: "POST",

        headers: { "content-type": "application/json" },

        credentials: "include",

        body: JSON.stringify({

          theme: themeId,

          snapshot: this.getSnapshot?.() ?? null,

        }),

      });

      if (!res.ok) return false;

      const data = await res.json();

      if (data?.snapshot) this.app.fire("scene:http-resync", data.snapshot);

      else if (data?.finishes) this._applyFinishes(data.finishes);

      return data?.ok !== false;

    } catch {

      return false;

    }

  }



  /** Parse one SSE block; returns 1 if it represents a furniture placement. */

  _handle(raw) {

    let event = "message";

    let data = "";

    for (const line of raw.split("\n")) {

      if (line.startsWith("event:")) event = line.slice(6).trim();

      else if (line.startsWith("data:")) data += line.slice(5).trim();

    }

    let payload = {};

    try {

      payload = data ? JSON.parse(data) : {};

    } catch {

      /* ignore malformed */

    }



    switch (event) {

      case "tool_call": {

        if (payload.name === "procedural_place_furniture") {

          this.app.fire("ai:status", { text: `Placing ${payload.input?.asset_id ?? "item"}…`, busy: true });

          return 1;

        }

        this.app.fire("ai:status", { text: PRETTY[payload.name] ?? `${payload.name}…`, busy: true });

        return 0;

      }

      case "scene_cmd": {

        const method = payload.method;

        if (typeof method === "string") this.app.fire(`net:${method}`, payload.params ?? {});

        if (method === "furniture:spawn") return 1;

        return 0;

      }

      case "snapshot":

        if (payload?.placements || payload?.room) this.app.fire("scene:http-resync", payload);

        return 0;

      case "assistant":

        if (payload.text) this.app.fire("ai:status", { text: payload.text, busy: true });

        return 0;

      case "info":

        if (payload.message) this.app.fire("ai:status", { text: payload.message, busy: true });

        return 0;

      case "error":

        this.app.fire("ai:status", { text: `Error: ${payload.message ?? "agent error"}`, busy: false, error: true });

        return 0;

      default:

        return 0;

    }

  }

}



export default AgentClient;

